/**
 * The Sunday recap: the week's GiviSnaps in one collage, from the small
 * copies kept for it (shared/recap.ts, functions/src/recap.ts). Out on
 * Sunday morning, and gone when the next one replaces it.
 *
 * A card on the GiviSnaps page (and the desktop's tiles) shows a corner of
 * it; the card opens the whole collage over the app, at #/recap, which is
 * also where the Sunday notification lands.
 */
import type { CSSProperties } from 'react';
import { DAILY_NAME } from '../../../shared/constants';
import { RECAP_NAME, inOrder, weekWords, type RecapDoc } from '../../../shared/recap';
import type { UserDoc } from '../../../shared/types';
import { avatarOf, urlForKey } from '../data';
import { BackCloses, go, goBack } from '../router';
import { Avatar, IconClose } from '../ui';
import { Photo } from '../Photo';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function weekdayOf(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay()]!;
}

const countWords = (n: number) => `${n} ${n === 1 ? DAILY_NAME : `${DAILY_NAME}s`}`;

/** A corner of the week, and the way into the rest of it. */
export function RecapCard({ recap }: { recap: RecapDoc }) {
  const shown = inOrder(recap.entries).slice(0, 4);
  return (
    <button type="button" className="recap-card" onClick={() => go('recap')}>
      <span className="recap-card-shots" aria-hidden="true">
        {shown.map((entry) => (
          <Photo key={entry.key} src={urlForKey(entry.key)} alt="" loading="lazy" draggable={false} />
        ))}
      </span>
      <span className="recap-card-words">
        <span className="recap-card-title">{RECAP_NAME}</span>
        <span className="recap-card-meta">
          {weekWords(recap.week)} · {countWords(recap.entries.length)}
        </span>
      </span>
    </button>
  );
}

/** The whole week, over the app. */
export function RecapView({ recap, users }: { recap: RecapDoc; users: Record<string, UserDoc> }) {
  const close = () => goBack('today');
  const name = (uid: string) => users[uid]?.displayName ?? 'Someone';
  return (
    <div className="recap-view" role="dialog" aria-label={RECAP_NAME}>
      <BackCloses onBack={close} />
      <div className="recap-head">
        <span className="recap-head-words">
          <h2 className="recap-title">{RECAP_NAME}</h2>
          <span className="recap-meta">
            {weekWords(recap.week)} · {countWords(recap.entries.length)} · gone next Sunday
          </span>
        </span>
        <button type="button" className="icon-btn" aria-label="Close" onClick={close}>
          <IconClose size={20} />
        </button>
      </div>
      <div className="recap-grid">
        <div className="recap-columns">
        {inOrder(recap.entries).map((entry) => (
          <figure
            key={entry.key}
            className="recap-shot"
            data-key={entry.key}
            style={{ '--ar': `${entry.width} / ${entry.height}` } as CSSProperties}
          >
            <Photo src={urlForKey(entry.key)} alt={`${name(entry.authorUid)}, ${weekdayOf(entry.day)}`} loading="lazy" draggable={false} />
            <figcaption className="recap-shot-who">
              <Avatar name={name(entry.authorUid)} photoURL={avatarOf(users[entry.authorUid])} size={20} />
              <span>{name(entry.authorUid)}</span>
              <span className="recap-shot-day">{weekdayOf(entry.day)}</span>
            </figcaption>
          </figure>
        ))}
        </div>
      </div>
    </div>
  );
}
